import React, { useCallback, useEffect, useState } from "react";
import { Edit2, Trash2, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Upload } from "lucide-react";
import { toast } from "react-toastify";
import UserForm from "./UserForm";
import { createUser, deleteUser, getUsers, downloadImportTemplate, importUsersFromExcel } from "../../services/UserService";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Tooltip,
} from "@mui/material";

const configDate = {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: false,
  timeZone: "Asia/Ho_Chi_Minh", // hoặc remove nếu dùng UTC
};

const generateConfirmationPin = () =>
  String(Math.floor(100000 + Math.random() * 900000));

export default function Users() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    role: "",
    fullName: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 6;
  const [isEditing, setIsEditing] = useState(null);
  const [listUsers, setListUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [expandedUserClasses, setExpandedUserClasses] = useState({});
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    type: "",
    pin: "",
    inputPin: "",
    targetLabel: "",
    payload: null,
  });
  const [importDialog, setImportDialog] = useState({
    open: false,
    isLoading: false,
    result: null,
  });
  const fileInputRef = React.useRef(null);

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `User_Import_Template_${new Date().getTime()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success("Tải template thành công");
    } catch (error) {
      const message = error?.response?.data?.message || "Tải template thất bại";
      toast.error(message);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Reset file input
    event.target.value = "";

    // Validate file type
    const allowedTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
      "text/csv",
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error("File phải là Excel (.xlsx, .xls) hoặc CSV (.csv)");
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File quá lớn (tối đa 10MB)");
      return;
    }

    setImportDialog((prev) => ({
      ...prev,
      isLoading: true,
      result: null,
    }));

    try {
      const response = await importUsersFromExcel(file);
      setImportDialog((prev) => ({
        ...prev,
        open: true,
        isLoading: false,
        result: response?.data,
      }));
      
      // Refresh users list
      handleFetch();
      toast.success(response?.message);
    } catch (error) {
      const message = error?.response?.data?.message || "Import thất bại";
      setImportDialog((prev) => ({
        ...prev,
        open: true,
        isLoading: false,
        result: {
          error: true,
          message,
          errors: error?.response?.data?.errors,
        },
      }));
      toast.error(message);
    }
  };

  const closeImportDialog = () => {
    setImportDialog({
      open: false,
      isLoading: false,
      result: null,
    });
  };

  const handleFetch = useCallback(async () => {
    try {
      const response = await getUsers(currentPage, limit, appliedSearch);
      setListUsers(response?.data);
      setTotalPages(response?.totalPages);
      setCurrentPage(response?.currentPage);
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  }, [currentPage, limit, appliedSearch]);

  useEffect(() => {
    handleFetch();
  }, [currentPage, handleFetch]);

  const handleEditUser = (user) => {
    setIsEditing(true);
    setFormData(user);
  };

  const closeConfirmationDialog = () => {
    setConfirmationDialog({
      open: false,
      type: "",
      pin: "",
      inputPin: "",
      targetLabel: "",
      payload: null,
    });
  };

  const openConfirmationDialog = ({ type, targetLabel, payload }) => {
    setConfirmationDialog({
      open: true,
      type,
      pin: generateConfirmationPin(),
      inputPin: "",
      targetLabel,
      payload,
    });
  };

  const performDeleteUser = async (id) => {
    try {
      const res = await deleteUser(id);
      toast.success(res?.message);
      setListUsers(listUsers.filter((user) => user?._id !== id));
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  const handleDeleteUser = (user) => {
    openConfirmationDialog({
      type: "delete",
      targetLabel: user?.fullName || user?.email || user?._id,
      payload: { id: user?._id },
    });
  };

  const handleInsertUser = async () => {
    if (validateForm()) {
      try {
        const res = await createUser(formData);
        if (res && res.data) {
          setListUsers([res.data, ...listUsers]);
          handleFetch();
          toast.success(res?.message);
          setFormData({
            email: "",
            password: "",
            role: "",
            fullName: "",
          });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message);
      }
    }
  };

  const performUpdateUser = async (payload) => {
    try {
      const res = await createUser(payload);
      if (res && res.data) {
        setListUsers((prev) =>
          prev.map((e) => (e._id === res.data?._id ? res.data : e))
        );
        handleFetch();
        toast.success(res?.message);
        setFormData({
          email: "",
          password: "",
          role: "",
          fullName: "",
        });
        setIsEditing(null);
      }
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message);
    }
  };

  const handleUpdateUser = async () => {
    if (!validateForm()) {
      return;
    }

    openConfirmationDialog({
      type: "update",
      targetLabel: formData?.fullName || formData?.email || formData?._id,
      payload: { ...formData },
    });
  };

  const handleConfirmAction = async () => {
    if (confirmationDialog.inputPin !== confirmationDialog.pin) {
      const refreshedPin = generateConfirmationPin();
      setConfirmationDialog((prev) => ({
        ...prev,
        pin: refreshedPin,
        inputPin: "",
      }));
      toast.error("Mã PIN không đúng. Hệ thống đã tạo mã mới để xác nhận lại.");
      return;
    }

    const { type, payload } = confirmationDialog;
    closeConfirmationDialog();

    if (type === "delete") {
      await performDeleteUser(payload?.id);
      return;
    }

    if (type === "update") {
      await performUpdateUser(payload);
    }
  };
  const validateForm = () => {
    if (!formData.email) {
      toast.error("Vui lòng nhập email");
      return false;
    }

    if (!formData.password && !isEditing) {
      toast.error("Vui lòng nhập password");
      return false;
    }

    if (!formData.fullName) {
      toast.error("Vui lòng nhập fullName");
      return false;
    }

    if (![0, 1].includes(formData.role)) {
      toast.error("Vui lòng nhập role");
      return false;
    }

    return true;
  };

  const handleChangeInputUser = (event) => {
    let { name, value } = event.target;
    if (name === "role") {
      const role = value === "Admin" ? 1 : 0;
      setFormData({
        ...formData,
        [name]: role,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    setAppliedSearch(searchQuery);
    setCurrentPage(1);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const toggleUserClasses = (userId) => {
    setExpandedUserClasses((prev) => ({
      ...prev,
      [userId]: !prev[userId],
    }));
  };

  const confirmationActionLabel =
    confirmationDialog.type === "delete" ? "xóa" : "cập nhật";

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">
        Quản Lý Người Dùng
      </h2>

      {/* User Form */}
      <UserForm
        isEditing={isEditing}
        formData={formData}
        handleInsertUser={handleInsertUser}
        handleChangeInputUser={handleChangeInputUser}
        handleUpdateUser={handleUpdateUser}
      />

      <Dialog
        open={confirmationDialog.open}
        onClose={closeConfirmationDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Xác nhận bằng mã PIN</DialogTitle>
        <DialogContent>
          <div className="space-y-4 pt-2">
            <Alert severity={confirmationDialog.type === "delete" ? "warning" : "info"}>
              Bạn đang yêu cầu {confirmationActionLabel} user
              {confirmationDialog.targetLabel ? `: ${confirmationDialog.targetLabel}` : ""}.
            </Alert>
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-center">
              <p className="text-sm text-gray-600 mb-2">Nhập đúng mã PIN bên dưới để xác nhận</p>
              <p className="text-3xl font-bold tracking-[0.4em] text-red-600">
                {confirmationDialog.pin}
              </p>
            </div>
            <TextField
              autoFocus
              fullWidth
              label="Nhap lai ma PIN 6 so"
              value={confirmationDialog.inputPin}
              onChange={(event) => {
                const nextValue = event.target.value.replace(/\D/g, "").slice(0, 6);
                setConfirmationDialog((prev) => ({
                  ...prev,
                  inputPin: nextValue,
                }));
              }}
              inputProps={{ maxLength: 6, inputMode: "numeric" }}
            />
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "0 24px 24px" }}>
          <Button onClick={closeConfirmationDialog} color="inherit">
            Hủy
          </Button>
          <Button
            onClick={handleConfirmAction}
            variant="contained"
            color={confirmationDialog.type === "delete" ? "error" : "primary"}
            disabled={confirmationDialog.inputPin.length !== 6}
          >
            Xác nhận
          </Button>
        </DialogActions>
      </Dialog>

      {/* Search Input */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between mb-6">
        <div className="relative w-full max-w-xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Tìm kiếm theo email, tên..."
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyPress={handleKeyPress}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
          />
        </div>
        <button
          onClick={handleSearch}
          className="ml-4 px-6 py-2 bg-gray-900 text-white font-medium rounded-lg hover:bg-gray-800 transition-colors"
        >
          Tìm kiếm
        </button>
      </div>

      {/* Import Section */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={handleDownloadTemplate}
          className="flex items-center justify-center gap-2 px-6 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100 transition-colors border border-blue-200"
          title="Tải template Excel để import hàng loạt"
        >
          <Download size={18} />
          Tải Template
        </button>
        <button
          onClick={handleImportClick}
          disabled={importDialog.isLoading}
          className="flex items-center justify-center gap-2 px-6 py-2 bg-green-50 text-green-600 font-medium rounded-lg hover:bg-green-100 transition-colors border border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Import users từ file Excel"
        >
          <Upload size={18} />
          {importDialog.isLoading ? "Đang import..." : "Import Excel"}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={handleFileSelect}
          className="hidden"
          aria-label="Chọn file Excel"
        />
      </div>

      {/* Import Result Dialog */}
      <Dialog
        open={importDialog.open}
        onClose={closeImportDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle className="bg-gray-50 border-b">
          {importDialog.result?.error ? "❌ Import Thất Bại" : "✅ Kết Quả Import"}
        </DialogTitle>
        <DialogContent className="pt-6">
          <div className="space-y-4">
            {importDialog.result?.error ? (
              <>
                <Alert severity="error">{importDialog.result?.message}</Alert>
                {importDialog.result?.errors && importDialog.result.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-red-900 mb-2">Các lỗi:</p>
                    <ul className="text-sm text-red-700 space-y-1">
                      {importDialog.result.errors.map((error, idx) => (
                        <li key={idx} className="list-disc list-inside">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <>
                <Alert severity="success">{importDialog.result?.message}</Alert>
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">
                      {importDialog.result?.totalRows}
                    </p>
                    <p className="text-xs text-blue-700 font-medium">Tổng Dòng</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">
                      {importDialog.result?.successCount}
                    </p>
                    <p className="text-xs text-green-700 font-medium">Thành Công</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-red-600">
                      {importDialog.result?.failureCount}
                    </p>
                    <p className="text-xs text-red-700 font-medium">Lỗi</p>
                  </div>
                </div>

                {importDialog.result?.errors && importDialog.result.errors.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm font-semibold text-yellow-900 mb-2">Các lỗi:</p>
                    <ul className="text-sm text-yellow-700 space-y-1 max-h-48 overflow-y-auto">
                      {importDialog.result.errors.map((error, idx) => (
                        <li key={idx} className="list-disc list-inside">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px" }}>
          <Button onClick={closeImportDialog} variant="contained">
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Users Table */}
      <div className="bg-white shadow-sm border border-gray-100 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">ID</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tên người dùng</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Số điện thoại</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Vai trò</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ngày tạo</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Lớp</th>
                <th className="px-6 py-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Hành động</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {listUsers.length > 0 ? (
                listUsers.map((user) => (
                  <tr key={user?._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Tooltip title={user?._id} placement="top">
                        <span className="text-sm font-medium text-gray-900 cursor-help bg-gray-100 px-2 py-1 rounded">
                          {user?._id?.slice(0, 5)}...{user?._id?.slice(-5)}
                        </span>
                      </Tooltip>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-medium">
                      {user?.email}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <div className="flex items-center gap-3">
                         <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold text-xs">
                            {user?.fullName?.charAt(0)?.toUpperCase()}
                         </div>
                         {user?.fullName}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {user?.phone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user?.role === 1 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {user?.role === 1 ? "Admin" : "User"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(user?.createdAt).toLocaleDateString("vi-VN", configDate)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {user?.classes?.length > 0 ? (
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              onClick={() => toggleUserClasses(user?._id)}
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors"
                              title={user.classes[0]?.classCode}
                            >
                              {user.classes[0]?.name}
                            </button>
                            {user?.classes?.length > 1 ? (
                              <button
                                type="button"
                                onClick={() => toggleUserClasses(user?._id)}
                                className="text-xs text-blue-600 hover:text-blue-700 font-medium underline"
                              >
                                {expandedUserClasses[user?._id]
                                  ? "Thu gọn"
                                  : `+${user.classes.length - 1} lớp`}
                              </button>
                            ) : null}
                          </div>

                          {expandedUserClasses[user?._id] && user?.classes?.length > 1 ? (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {user.classes.slice(1).map((cls) => (
                                <span
                                  key={cls._id}
                                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
                                  title={cls.classCode}
                                >
                                  {cls.name}
                                </span>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleEditUser(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Sửa"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDeleteUser(user)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Xóa"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    Không tìm thấy dữ liệu
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 bg-gray-50 border-t border-gray-100 gap-3">
          <span className="text-sm text-gray-500">
            Trang <span className="font-semibold text-gray-700">{currentPage}</span> / <span className="font-semibold text-gray-700">{totalPages}</span>
          </span>

          <div className="flex items-center gap-1">
            {/* First page */}
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Trang đầu"
            >
              <ChevronsLeft size={16} />
            </button>

            {/* Prev */}
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Trang trước"
            >
              <ChevronLeft size={16} />
            </button>

            {/* Page numbers */}
            {(() => {
              const pages = [];
              const delta = 2;
              const left = Math.max(1, currentPage - delta);
              const right = Math.min(totalPages, currentPage + delta);

              if (left > 1) {
                pages.push(1);
                if (left > 2) pages.push("...");
              }
              for (let p = left; p <= right; p++) pages.push(p);
              if (right < totalPages) {
                if (right < totalPages - 1) pages.push("...");
                pages.push(totalPages);
              }

              return pages.map((page, idx) =>
                page === "..." ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-gray-400 select-none">…</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`min-w-[34px] h-[34px] rounded-lg text-sm font-medium transition-colors ${
                      page === currentPage
                        ? "bg-gray-900 text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    {page}
                  </button>
                )
              );
            })()}

            {/* Next */}
            <button
              onClick={() => setCurrentPage((p) => (p < totalPages ? p + 1 : p))}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Trang sau"
            >
              <ChevronRight size={16} />
            </button>

            {/* Last page */}
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Trang cuối"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
